import { useState, useEffect, useCallback, useRef } from 'react';
import { Modal } from 'react-bootstrap-v5';
import moment from 'moment';
import Swal from 'sweetalert2';
import { IArquivoOperacao } from '../../interfaces/IArquivoOperacao';
import ArquivoOperacaoService from '../../services/ArquivoOperacaoService';
import { VisualizarPosicaoFinanceiraModal } from './arquivos-cartao';

const TIPOS_LABEL: Record<string, string> = {
    POSICAO_FINANCEIRA: 'Posição Financeira'
};

const PER_PAGE = 10;

function statusInfo(arquivo: IArquivoOperacao): { label: string; color: string; bg: string } {
    if (arquivo.arquivoErro)
        return { label: 'ERRO', color: '#d9534f', bg: '#d9534f22' };
    if (arquivo.arquivoProcessando)
        return { label: 'PROCESSANDO', color: '#f0ad4e', bg: '#f0ad4e22' };
    if (arquivo.arquivoProcessou)
        return { label: 'CONCLUÍDO', color: '#5cb85c', bg: '#5cb85c22' };
    if (arquivo.arquivoIniciouProcesso)
        return { label: 'INICIADO', color: '#009ef7', bg: '#009ef722' };
    return { label: 'AGUARDANDO', color: '#6c757d', bg: '#6c757d22' };
}

interface Props {
    esteiraID: number;
    show: boolean;
    toggleModal: () => void;
}

function ModalArquivosOperacao({ esteiraID, show, toggleModal }: Props) {
    const [dados, setDados] = useState<IArquivoOperacao[]>([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [visualizarArquivo, setVisualizarArquivo] = useState<IArquivoOperacao | null>(null);

    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const buscar = useCallback(() => {
        if (!esteiraID) return;
        setLoading(true);
        ArquivoOperacaoService.listar(esteiraID, page, PER_PAGE)
            .then(res => {
                setDados(res.items);
                setTotal(res.total);
                setTotalPages(res.totalPages);
            })
            .catch(err => console.error('Erro ao buscar arquivos:', err))
            .finally(() => setLoading(false));
    }, [esteiraID, page]);

    useEffect(() => {
        if (show && esteiraID) {
            buscar();
        }
    }, [show, esteiraID, buscar]);

    const handleUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        try {
            const { signedUrl, generatedFileName } = await ArquivoOperacaoService.gerarUrl(file.name, file.type);
            await ArquivoOperacaoService.enviarParaStorage(signedUrl, file);
            await ArquivoOperacaoService.registrar(esteiraID, 'POSICAO_FINANCEIRA', generatedFileName, file.name);

            Swal.fire({ text: 'Arquivo enviado e enfileirado para processamento!', icon: 'success', timer: 3000, showConfirmButton: false });
            buscar();
        } catch {
            Swal.fire({ text: 'Erro ao enviar o arquivo. Tente novamente.', icon: 'error', timer: 3000, showConfirmButton: false });
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    }, [esteiraID, buscar]);

    const handlePage = (p: number) => {
        if (p >= 1 && p <= totalPages) setPage(p);
    };

    return (
        <>
            {visualizarArquivo && (
                <VisualizarPosicaoFinanceiraModal
                    esteiraId={esteiraID}
                    arquivoOperacaoId={visualizarArquivo.id}
                    nomeArquivo={visualizarArquivo.nomeOriginal ?? ''}
                    onClose={() => setVisualizarArquivo(null)}
                />
            )}

            <Modal size='lg' centered show={show} onHide={toggleModal}>
                <div className='modal-content' style={{ backgroundColor: '#F4F5F7' }}>
                    <div className='modal-header'>
                        <h5 className='modal-title titulo-modal'>Arquivos da Esteira</h5>
                        <div className='btn btn-icon btn-sm btn-active-light-primary ms-2'>
                            <i onClick={toggleModal} className='fas fa-times' style={{ color: '#5E6397', cursor: 'pointer' }} />
                        </div>
                    </div>

                    <div className='modal-body'>
                        {/* Upload */}
                        <div className='card card-body mb-4 p-3' style={{ backgroundColor: '#fff', border: '1px solid #e4e6ef' }}>
                            <div className='fw-bold mb-2' style={{ fontSize: 13 }}>Enviar novo arquivo</div>
                            <div className='d-flex align-items-center gap-3 flex-wrap'>
                                <div className='d-flex align-items-center gap-2'>
                                    <label className='fw-semibold mb-0 text-nowrap' style={{ fontSize: 13 }}>Tipo:</label>
                                    <select
                                        className='form-select form-select-sm'
                                        style={{ minWidth: 170 }}
                                        disabled={uploading}
                                    >
                                        <option value='POSICAO_FINANCEIRA'>Posição Financeira</option>
                                    </select>
                                </div>
                                <div className='d-flex align-items-center gap-2'>
                                    <input
                                        ref={fileInputRef}
                                        type='file'
                                        className='form-control form-control-sm'
                                        style={{ maxWidth: 260 }}
                                        disabled={uploading}
                                        onChange={handleUpload}
                                    />
                                    {uploading && (
                                        <span className='spinner-border spinner-border-sm text-primary' />
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Contagem */}
                        <div className='d-flex justify-content-end mb-4'>
                            <span className='text-muted' style={{ fontSize: 12 }}>
                                {total} registro{total !== 1 ? 's' : ''}
                            </span>
                        </div>

                        {/* Tabela */}
                        {loading ? (
                            <div className='d-flex justify-content-center py-5'>
                                <span className='spinner-border spinner-border-sm text-primary me-2 mt-1' />
                                <span>Carregando...</span>
                            </div>
                        ) : (
                            <>
                                {dados.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '30px', color: '#6c757d' }}>
                                        Nenhum arquivo encontrado.
                                    </div>
                                ) : (
                                    <div style={{ overflowX: 'auto' }}>
                                        <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3 mb-0'>
                                            <thead>
                                                <tr className='fw-bold text-muted' style={{ fontSize: 12 }}>
                                                    <th>Nome</th>
                                                    <th>Tipo</th>
                                                    <th>Enviado em</th>
                                                    <th>Conclusão</th>
                                                    <th className='text-center'>Status</th>
                                                    <th className='text-center'>Ações</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {dados.map(arquivo => {
                                                    const st = statusInfo(arquivo);
                                                    return (
                                                        <tr key={arquivo.id}>
                                                            <td style={{ fontSize: 12, maxWidth: 200 }}>
                                                                <span
                                                                    title={arquivo.nomeOriginal}
                                                                    style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                                >
                                                                    {arquivo.nomeOriginal}
                                                                </span>
                                                            </td>
                                                            <td style={{ fontSize: 12 }}>
                                                                {arquivo.tipo
                                                                    ? (TIPOS_LABEL[arquivo.tipo] ?? arquivo.tipo)
                                                                    : <span className='text-muted'>—</span>}
                                                            </td>
                                                            <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                                                {moment(arquivo.dataCadastro).format('DD/MM/YYYY HH:mm')}
                                                            </td>
                                                            <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                                                {arquivo.dataFimProcesso
                                                                    ? moment(arquivo.dataFimProcesso).format('DD/MM/YYYY HH:mm')
                                                                    : <span className='text-muted'>—</span>}
                                                            </td>
                                                            <td className='text-center'>
                                                                <span style={{
                                                                    fontSize: 10,
                                                                    fontWeight: 'bold',
                                                                    color: st.color,
                                                                    backgroundColor: st.bg,
                                                                    padding: '2px 8px',
                                                                    borderRadius: 4,
                                                                    whiteSpace: 'nowrap'
                                                                }}>
                                                                    {st.label}
                                                                </span>
                                                            </td>
                                                            <td className='text-center'>
                                                                {arquivo.tipo === 'POSICAO_FINANCEIRA' && arquivo.arquivoProcessou && !arquivo.arquivoErro && (
                                                                    <button
                                                                        className='btn btn-sm btn-light-primary'
                                                                        style={{ fontSize: 11, padding: '2px 10px' }}
                                                                        onClick={() => setVisualizarArquivo(arquivo)}
                                                                    >
                                                                        Visualizar
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* Paginação */}
                                {totalPages > 1 && (
                                    <div className='d-flex justify-content-between align-items-center mt-3'>
                                        <span style={{ fontSize: 12, color: '#6c757d' }}>
                                            Página {page} de {totalPages}
                                        </span>
                                        <div className='d-flex gap-1'>
                                            <button
                                                className='btn btn-sm btn-light'
                                                disabled={page === 1}
                                                onClick={() => handlePage(1)}
                                            >«</button>
                                            <button
                                                className='btn btn-sm btn-light'
                                                disabled={page === 1}
                                                onClick={() => handlePage(page - 1)}
                                            >‹</button>
                                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                                                const p = start + i;
                                                return (
                                                    <button
                                                        key={p}
                                                        className={`btn btn-sm ${p === page ? 'btn-primary' : 'btn-light'}`}
                                                        onClick={() => handlePage(p)}
                                                    >
                                                        {p}
                                                    </button>
                                                );
                                            })}
                                            <button
                                                className='btn btn-sm btn-light'
                                                disabled={page === totalPages}
                                                onClick={() => handlePage(page + 1)}
                                            >›</button>
                                            <button
                                                className='btn btn-sm btn-light'
                                                disabled={page === totalPages}
                                                onClick={() => handlePage(totalPages)}
                                            >»</button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </Modal>
        </>
    );
}

export default ModalArquivosOperacao;
